<template>
    <section class="create-zone">
        <Loader :loading="loading" v-if="loading" :fixed="true"></Loader>
        <div class="create-zone-title d-flex align-items-center">
            <i class="fa fa-plus fa-sm"></i> <span>{{ $t("headerAdmin.zone.add") }}</span>
        </div>

        <div class="container create-zone-search">
            <p class="create-zone-msg mb-4 mt-5">{{ $t("zone.createSearchMessage") }}</p>
            <form class="form-search" v-on:submit.prevent="searchZone">
                <div class="input-search row">
                    <div class="col d-flex align-items-center justify-content-center">
                        <input v-model.trim="search" id="search" type="text" />
                        <ClientColorButton
                            :background-color="currentBrand.linksColor"
                            :inactive-color="search.length === 0"
                            :disabled="search.length === 0"
                        >
                            {{ $t("createUser.searchIt") }}
                        </ClientColorButton>
                    </div>
                </div>
            </form>

            <div v-if="(newZone || updateZone) && searchOk" class="create-zone-form mt-4">
                <div class="create-zone-form-container container">
                    <div class="form-group row">
                        <DefaultTextInputBloc
                            :label="$t('zone.name')"
                            :required="true"
                            v-model="zone.name"
                            :max-length="99"
                            :disabled="updateZone"
                            :readonly="updateZone"
                        ></DefaultTextInputBloc>
                    </div>
                    <br />
                    <div class="form-group row">
                        <DefaultTextInputBloc
                            :label="$t('zone.abbreviation')"
                            :required="false"
                            v-model="zone.abbreviation"
                            :max-length="10"
                            :disabled="updateZone"
                            :readonly="updateZone"
                        ></DefaultTextInputBloc>
                    </div>
                    <br />
                    <div class="form-group row">
                        <label class="col-12 col-sm-3 col-form-label label">{{ $t("zone.brand") }} *</label>
                        <div class="col-12 col-sm-9">
                            <Multiselect
                                v-model="brandSelected"
                                :options="brands"
                                label="name"
                                track-by="name"
                                :multiple="true"
                                :select-label="''"
                                :searchable="true"
                                :close-on-select="true"
                                :show-labels="true"
                                :placeholder="'select the brands'"
                            ></Multiselect>
                        </div>
                    </div>
                    <br />
                    <div class="form-group row">
                        <label class="col-12 col-sm-3 col-form-label label">{{ $t("zone.section") }} *</label>
                        <div class="col-sm-9">
                            <Multiselect
                                v-model="sectionSelected"
                                :options="sections"
                                label="name"
                                track-by="name"
                                :multiple="false"
                                :select-label="''"
                                :searchable="true"
                                :close-on-select="true"
                                :show-labels="true"
                                :placeholder="'select the section'"
                                :disabled="updateZone"
                                :readonly="updateZone"
                            ></Multiselect>
                        </div>
                    </div>
                    <br />
                    <div class="form-group row">
                        <DefaultTextareaInputBloc
                            :label="$t('zone.description')"
                            :required="false"
                            v-model="zone.description"
                            :max-length="1000"
                            :disabled="updateZone"
                            :readonly="updateZone"
                        ></DefaultTextareaInputBloc>
                    </div>
                    <br />
                    <div class="form-group row">
                        <DefaultTextInputBloc
                            :label="$t('zone.refClient')"
                            :required="false"
                            v-model="zone.refClient"
                            :max-length="100"
                            :disabled="updateZone"
                            :readonly="updateZone"
                        ></DefaultTextInputBloc>
                    </div>

                    <hr class="hr my-5 border-dark" />

                    <div class="mb-4">
                        <span class="label-relation-type">{{
                            $t("zone.relationBetween", {
                                zone: zone.name.toUpperCase(),
                                brand: brandSelected
                                    .map(b => b.name)
                                    .join(", ")
                                    .toUpperCase()
                            })
                        }}</span>
                        <Multiselect
                            class="mt-3"
                            v-model="zone.relationType"
                            :options="relations"
                            label="displayName"
                            track-by="name"
                            :multiple="false"
                            :select-label="''"
                            :searchable="true"
                            :close-on-select="true"
                            :show-labels="true"
                            :placeholder="'Select the relation'"
                        ></Multiselect>
                    </div>

                    <label for="allowCatalogForecast" class="row label-checkbox pointer d-flex align-items-center">
                        <div class="col">
                            <span class="label-title label-allow-forecast">{{ $t("zone.allowCatalogForecast") }}</span>
                        </div>
                        <div class="col">
                            <span v-show="zone.allowForecast" class="rem3rch-checkbox rem3rch-checkbox-xs"> <i class="fa fa-check"></i> </span>
                            <span v-if="!zone.allowForecast" class="rem3rch-checkbox rem3rch-checkbox-xs"></span>
                        </div>
                        <input id="allowCatalogForecast" type="checkbox" class="d-none" v-model="zone.allowForecast" />
                    </label>

                    <label for="allowCatalogFirmOrder" class="row label-checkbox pointer d-flex align-items-center">
                        <div class="col">
                            <span class="label-title label-allow-firmorder">{{ $t("zone.allowCatalogFirmOrder") }}</span>
                        </div>
                        <div class="col">
                            <span v-show="zone.allowFirmOrder" class="rem3rch-checkbox rem3rch-checkbox-xs"> <i class="fa fa-check"></i> </span>
                            <span v-if="!zone.allowFirmOrder" class="rem3rch-checkbox rem3rch-checkbox-xs"></span>
                        </div>
                        <input id="allowCatalogFirmOrder" type="checkbox" class="d-none" v-model="zone.allowFirmOrder" />
                    </label>
                </div>
            </div>

            <div v-if="!newZone && !updateZone && searchOk">
                <div class="table-responsive mt-4" v-if="!loading">
                    <table class="create-zone-table">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>{{ $trad.headerAdmin.zone.client_ref }}</th>
                                <th>{{ $trad.headerAdmin.zone.name }}</th>
                                <th>{{ $trad.headerAdmin.zone.brands }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="zone in zonesFound" :key="zone.id">
                                <td class="id px-3">
                                    <span>{{ zone.id }}</span>
                                </td>
                                <td class="ref px-3">
                                    <span>{{ zone.refClient ? zone.refClient : "-" }}</span>
                                </td>
                                <td class="name px-3">
                                    <span>{{ zone.name }}</span>
                                </td>
                                <td class="brands px-3">
                                    <span>
                                        <template v-for="(brandZone, key) in brandZonesFiltered(zone)">
                                            <span
                                                :key="key"
                                                :class="{ 'brand-inactive': !brandZone.active }"
                                                :title="!brandZone.active ? $t('zone.brandInactive') : ''"
                                            >
                                                <template v-if="key > 0">{{ "\xa0\u2022 " }}</template
                                                >{{ brandZone.brand.name }}
                                            </span>
                                        </template>
                                    </span>
                                </td>
                                <td class="selectIt">
                                    <ClientColorButton
                                        background-color="#333"
                                        @click="hasZoneAccess(brandZonesFiltered(zone)) ? goToZoneCard(zone.id) : forceUpdateZone(zone);"
                                        >{{ $t("zone.selectIt") }}</ClientColorButton
                                    >
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div class="row d-flex justify-content-center mt-5">
                <div class="col d-flex" :class="searchOk && !newZone ? '' : 'justify-content-center'">
                    <ClientColorButton :inactiveColor="true" @click.prevent="cancel" class-name="mr-3">{{
                        searchOk ? $t("common.cancel") : $t("zone.backToZoneList")
                    }}</ClientColorButton>
                </div>
                <div class="col d-flex" :class="searchOk && !newZone && !updateZone ? 'justify-content-end' : 'justify-content-center'">
                    <ClientColorButton v-if="searchOk && !newZone && !updateZone" @click="forceNewZone();" :background-color="getBrand.linksColor">
                        {{ $t("zone.createNew") }}
                    </ClientColorButton>
                    <ClientColorButton
                        v-if="searchOk && (newZone || updateZone)"
                        @click="newZone ? createZone() : addZone(zone);"
                        :background-color="getBrand.linksColor"
                        :inactiveColor="!isFormValidated()"
                        :disabled="!isFormValidated()"
                    >
                        {{ $t("zone.save") }}
                    </ClientColorButton>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import Spinner from "vue-simple-spinner";
import cacheService from "@/services/cache";
import Multiselect from "vue-multiselect";
import TextInputBloc from "@/components/ui/Form/TextInputBloc";
import TextareaInputBloc from "@/components/ui/Form/TextareaInputBloc";
import zoneService from "@/services/zone";
import sectionService from "@/services/section";
import { mapGetters, mapState } from "vuex";
import Loader from "@/components/loader/loader";
import DefaultTextInputBloc from "@/components/ui/Form/DefaultTextInputBloc";
import DefaultTextareaInputBloc from "@/components/ui/Form/DefaultTextareaInputBloc";
import { removeNbsp } from "../../../utils/string";

export default {
    data: () => ({
        brands: [],
        brandSelected: [],
        sections: [],
        relations: [
            {
                name: "affiliate",
                displayName: "AFFILIATE"
            },
            {
                name: "export",
                displayName: "EXPORT"
            }
        ],
        sectionSelected: null,
        refForSelect: [],
        currentBrandId: cacheService.getCurrentBrandId(),
        zone: {
            name: "",
            abbreviation: "",
            brands: [],
            section: {
                id: null
            },
            description: "",
            refClient: "",
            relationType: {
                name: "affiliate",
                displayName: "AFFILIATE"
            },
            allowForecast: false,
            allowFirmOrder: false
        },
        loading: false,
        selectedZone: null,
        searchOk: false,
        zonesFound: [],
        newZone: false,
        updateZone: false,
        search: ""
    }),
    props: {
        parentLoading: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    watch: {},
    components: {
        DefaultTextareaInputBloc,
        DefaultTextInputBloc,
        Loader,
        Spinner,
        Multiselect,
        TextInputBloc,
        TextareaInputBloc
    },
    computed: {
        ...mapGetters({ getBrand: "brand/getBrand" }),
        ...mapState("currentUser", ["currentBrand"])
    },
    methods: {
        brandZonesFiltered(zone) {
            let result;
            if (this.isSuperAdmin()) {
                result = [...zone.brandZone];
            } else {
                const brands = cacheService.getBrands();
                result = zone.brandZone.filter(bz => brands.some(b => b.id === bz.brand.id));
            }
            return result.sort((a, b) => a.brand.name.localeCompare(b.brand.name));
        },
        hasZoneAccess(brandZones) {
            return brandZones.some(bz => bz.brand.id === this.currentBrand.id);
        },
        searchZone() {
            this.loading = true;
            this.searchOk = false;
            this.newZone = false;
            this.updateZone = false;
            this.search = removeNbsp(this.search);

            zoneService
                .search(this.search)
                .then(res => {
                    this.loading = false;
                    if (!res.length) {
                        this.zone.name = this.search;
                        this.newZone = true;
                    } else {
                        this.zonesFound = res;
                    }
                    this.searchOk = true;
                })
                .catch(() => {
                    this.loading = false;
                    this.dialogError("error");
                });
        },
        createZone() {
            this.zone.brands = this.brandSelected.map(bs => bs.id);
            this.zone.section.id = this.sectionSelected.id;
            this.zone.currentBrand = this.currentBrand.id;

            try {
                this.loading = true;

                this.zone.name = removeNbsp(this.zone.name);
                this.zone.refClient = removeNbsp(this.zone.refClient);

                zoneService
                    .create(this.zone)
                    .then(zoneCreated => {
                        this.loading = false;
                        this.dialogSuccess("success");
                        this.goToZoneCard(zoneCreated.id);
                    })
                    .catch(() => {
                        this.loading = false;
                        this.dialogError("error");
                    });
            } catch (e) {
                this.loading = false;
                this.dialogError("error");
            }
        },
        addZone(zone) {
            zone.brands = this.brandSelected.map(bs => bs.id);
            zone.section.id = this.sectionSelected.id;
            zone.currentBrand = this.currentBrand.id;
            try {
                this.loading = true;
                zoneService
                    .addZoneToBrand(zone.id, zone)
                    .then(() => {
                        this.loading = false;
                        this.dialogSuccess("success");
                        this.goToZoneCard(zone.id);
                    })
                    .catch(() => {
                        this.loading = false;
                        this.dialogError("error");
                    });
            } catch (e) {
                this.loading = false;
                this.dialogError("error");
            }
        },
        isFormValidated() {
            return this.zone.name !== "" && this.sectionSelected && this.sectionSelected.id !== null && this.brands.length;
        },
        goToZoneCard(zoneId) {
            this.$router.push({ path: "/zones/zone/" + zoneId });
        },
        cancel() {
            this.$router.push({ path: "/zones/zone-list" });
        },
        makeLabel(ref) {
            return (ref.zoneName + " (" + ref.zoneRefClient + ") - " + ref.brandName).toUpperCase();
        },
        forceNewZone() {
            this.newZone = true;
            this.zone.name = this.search;
        },
        forceUpdateZone(zone) {
            this.updateZone = true;
            this.zone.id = zone.id;
            this.zone.name = zone.name;
            this.zone.abbreviation = zone.abbreviation;
            this.zone.description = zone.description;
            this.zone.refClient = zone.refClient;
            let currentBrand = this.brands.find(brand => brand.id === this.currentBrandId);
            this.brandSelected = [currentBrand];
        }
    },
    async created() {
        this.brands = cacheService.getBrands();
        let currentBrand = this.brands.find(brand => brand.id === this.currentBrandId);
        this.brandSelected.push(currentBrand);
        this.sections = await sectionService.getAll();
        this.sectionSelected = this.sections.length ? this.sections[0] : null;
        let currentZones = this.brands.find(b => b.id === this.currentBrandId).zones;
        let zones = [];
        this.brands
            .map(b => {
                return { brandId: b.id, zones: b.zones };
            })
            .map(element => {
                let brandId = element.brandId;
                element.zones.forEach(z => {
                    if (!currentZones.some(cz => cz.id === z.id)) {
                        let v = {
                            brandId: brandId,
                            brandName: this.brands.find(b => b.id === brandId).name,
                            zoneId: z.id,
                            zoneName: z.name,
                            zoneRefClient: z.refClient
                        };
                        zones.push(v);
                    }
                });
            });
        this.refForSelect = zones.sort(function(a, b) {
            return a.zoneName.toLowerCase() < b.zoneName.toLowerCase() ? -1 : 1;
        });
    }
};
</script>

<style scoped lang="scss">
.create-zone {
    width: 100%;
    padding: 30px;
    background-color: #fff;

    &-title {
        color: #6f6f6f;

        span {
            margin-left: 5px;
            font-family: "Oswald", sans-serif;
            text-transform: uppercase;
            font-size: 32px;
        }
    }

    &-search {
        margin: 0 auto;
        .form-search {
            margin: 0 auto;
            padding-bottom: 30px;
            margin-bottom: 30px;

            input {
                width: 100%;
                max-width: 400px;
                height: 40px;
                border: 1px solid black;
                font-size: 15px;
                font-family: "oswald-regular", sans-serif;
                padding: 5px;
                display: inline-block;
                margin-right: 15px;

                &.is-invalid {
                    border-color: red;
                }

                @media (max-width: 992px) {
                    width: 100%;
                    max-width: 350px;
                }
            }

            .fa-search {
                color: #6f6f6f;
            }
        }
    }

    .label-relation-type,
    .label-allow-forecast,
    .label-allow-firmorder {
        font-family: "oswald", Sans-Serif;
        font-size: 18px;
        font-weight: bold;
        text-transform: initial;
    }

    &-table {
        border-collapse: collapse;
        width: 100%;
        font-size: 12px;

        tr {
            &.notfound {
                height: 150px;

                td {
                    border: #343434 1px solid;
                    background-color: #fff;
                }
            }
        }

        th {
            font-family: "Oswald", sans-serif;
            color: white;
            background: #343434;
            border: #343434 1px solid;
            text-transform: uppercase;
            text-align: center;
            font-weight: normal;
            vertical-align: center;
            padding: 15px 10px;
            font-size: 19px;
            @media (max-width: 1440px) {
                font-size: 16px;
            }
        }

        td {
            border-bottom: 5px solid #fff;
            font-size: 18px;
            line-height: 18px;
            padding: 10px;
            color: #262626;
            background-color: #f4f4f4;
            text-align: center;

            &.id {
                width: 90px;
                text-align: center;
                span {
                    font-size: 18px;
                    @media (max-width: 1440px) {
                        font-size: 16px;
                    }
                }
            }

            &.name,
            &.ref {
                min-width: 160px;
                span {
                    font-family: Oswald, Sans-Serif;
                    font-size: 20px;
                    @media (max-width: 1440px) {
                        font-size: 18px;
                    }
                }
            }

            &.brands {
                width: 400px;
                span {
                    line-height: 24px;
                    text-transform: capitalize;
                    font-size: 18px;
                    @media (max-width: 1440px) {
                        font-size: 16px;
                    }
                }
                & > span {
                    display: block;
                }
                .brand-inactive {
                    text-decoration: line-through;
                    opacity: 0.6;
                }
            }

            &.selectIt {
                padding-right: 0;
                width: 110px;
                text-align: right;
                background-color: #fff;
            }
        }
    }

    &-msg {
        text-align: center;
        font-size: 16px;
    }

    &-btn {
        padding: 10px 25px;
        background-color: #000;
        color: #fff;
        span {
            text-transform: uppercase;
            font-size: 14px !important;
            font-family: "Roboto", sans-serif;
        }
    }

    &-form {
        width: 100%;
        &-container {
            padding: 20px 300px;
            @media (max-width: 1440px) {
                padding: 20px 200px;
            }
            @media (max-width: 992px) {
                padding: 20px 0;
            }
            label:first-child {
                font-size: 16px;
                font-weight: bold;
                font-family: "Oswald", sans-serif;
            }
            input {
                border: 1px solid black;
                font-size: 15px;
                font-family: "oswald-regular", sans-serif;
                padding: 5px;
                &.is-invalid {
                    border-color: red;
                }
            }
        }
    }
}
</style>



// WEBPACK FOOTER //
// CreateZone.vue?e2c9ce18